import { Link } from "react-router-dom";
import { Check, X, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";

/** Comparison page — captures branded search traffic for "disboard alternative"
 * and "serverlistr vs disboard" queries. */
export default function VsDisboard() {
  const rows = [
    { feature: "Every listing verified by a Discord bot", us: true, them: false },
    { feature: "Staff-moderated queue (no scams / invite farms)", us: true, them: false },
    { feature: "Live bump countdown timer", us: true, them: false },
    { feature: "Real user reviews & ratings", us: true, them: false },
    { feature: "Modern, fast UI (2026 stack)", us: true, them: false },
    { feature: "Free to list your server", us: true, them: true },
    { feature: "Bump to gain visibility", us: true, them: true },
    { feature: "Featured slots (limited to 10)", us: true, them: false },
    { feature: "Auto-cleanup of dead servers", us: true, them: false },
    { feature: "Years of head start", us: false, them: true },
  ];
  return (
    <div className="pt-24 pb-16 max-w-4xl mx-auto px-4 sm:px-6" data-testid="vs-disboard-page">
      <SEO
        title="Serverlistr vs Disboard — Which Discord Server List Is Better?"
        description="Comparing Serverlistr and Disboard side-by-side: verified listings, moderation, features, and community. See why Serverlistr is the modern Discord server list for 2026."
        path="/serverlistr-vs-disboard"
        keywords="serverlistr vs disboard, disboard alternative, best discord server list, discord server directory, disboard replacement"
      />
      <Breadcrumbs items={[{ name: "Serverlistr vs Disboard" }]} />

      <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-black">
        Serverlistr vs Disboard
      </h1>
      <p className="mt-4 text-zinc-400 text-base sm:text-lg leading-relaxed max-w-2xl">
        Both are Discord server lists, but they take very different approaches.
        Disboard has been around longer; Serverlistr is the modern, curated
        alternative built in 2026. Here&apos;s the honest side-by-side.
      </p>

      <div className="mt-10 rounded-2xl border border-white/10 bg-[#0A0A0F] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-widest text-zinc-400">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Feature</th>
              <th className="text-center px-4 py-3 font-black text-neon-cyan">Serverlistr</th>
              <th className="text-center px-4 py-3 font-semibold text-zinc-500">Disboard</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.feature} className="border-t border-white/5">
                <td className="px-4 py-3 text-zinc-300">{r.feature}</td>
                <td className="px-4 py-3 text-center">
                  {r.us ? <Check className="inline w-5 h-5 text-emerald-400" /> : <X className="inline w-5 h-5 text-zinc-600" />}
                </td>
                <td className="px-4 py-3 text-center">
                  {r.them ? <Check className="inline w-5 h-5 text-zinc-400" /> : <X className="inline w-5 h-5 text-zinc-600" />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-10 space-y-6 prose prose-invert max-w-none prose-p:text-zinc-400 prose-headings:font-heading">
        <h2 className="font-heading text-2xl sm:text-3xl font-black">Why choose Serverlistr?</h2>
        <p>
          The biggest difference: <strong className="text-white">every server on Serverlistr is manually reviewed by staff</strong> before it appears on the site. Disboard listings are auto-approved, which is why you&apos;ll see scam servers, invite-farm gimmicks, and NSFW servers slip through their listings. Serverlistr doesn&apos;t allow that.
        </p>
        <p>
          If you own a Discord community and want to be discovered without competing against thousands of low-effort listings, Serverlistr&apos;s curation puts you in front of people actually looking for real communities.
        </p>
        <h2 className="font-heading text-2xl sm:text-3xl font-black">When Disboard is still better</h2>
        <p>
          Disboard has ~7 years of head start and hundreds of thousands of listings. If you&apos;re specifically looking for a niche server that was listed years ago, they might have it and we might not — yet. But we&apos;re growing fast and adding hundreds of new listings weekly.
        </p>
      </div>

      <div className="mt-12 rounded-2xl border border-neon-cyan/30 bg-neon-cyan/[0.03] p-6 text-center">
        <h2 className="font-heading text-xl sm:text-2xl font-black">Try Serverlistr</h2>
        <p className="mt-2 text-sm text-zinc-400">Browse thousands of verified Discord servers or list your own — free.</p>
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <Link to="/servers" className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-blurple hover:bg-blurple-hover text-white font-semibold">
            Browse Discord Servers <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/best/discord-servers" className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 font-semibold text-white">
            Top Discord Servers
          </Link>
        </div>
      </div>
    </div>
  );
}
